import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import SingleSelectExerciseList from "../component/SingleSelectExerciseList";
import { GoArrowLeft } from "react-icons/go";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBin4Line } from "react-icons/ri";

export const AdminManageRunrocketTrainingTemplateData = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();
  const { template_id } = useParams();

  const [templates, setTemplates] = useState([]);
  const [trainingData, setTrainingData] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Form states
  const [week, setWeek] = useState("Week 1");
  const [day, setDay] = useState("Monday");
  const [training_focus, setTrainingFocus] = useState("");
  const [reps, setReps] = useState("");
  const [distance, setDistance] = useState("");
  const [rest, setRest] = useState("");

  // Edit states
  const [editingFocusId, setEditingFocusId] = useState(null);
  const [newFocus, setNewFocus] = useState("");
  const [newExerciseId, setNewExerciseId] = useState(null);

  const [deleteId, setDeleteId] = useState(null);

  // Fetch exercises
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const res = await fetch(API_URL.GET_EXERCISE_LIBRARY_LIST_ADMIN, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Failed to fetch exercises");
        setExercises(data.exercises || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchExercises();
  }, [staff.token]);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch(
          `${API_URL.MANAGE_RUNROCKET_TRAINING_TEMPLATES}?page=1&limit=10`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Failed to fetch templates");
        setTemplates(data.data || []);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchTemplates();
  }, [staff.token]);

  // Fetch training data
  const fetchTrainingData = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_URL.GET_RUNROCKET_TRAINING_TEMPLATE_DATA}${template_id}`,
        {
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to fetch training data");
      setTrainingData(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainingData();
  }, [template_id]);

  // Pre-fill distance based on focus
  const handleTrainingFocusChange = (e) => {
    const selectedFocus = e.target.value;
    setTrainingFocus(selectedFocus);

    const distanceMap = {
      Technical: 20,
      "Speed - Strength": 15,
      Power: 10,
      "Strength - Speed": 5,
    };
    setDistance(distanceMap[selectedFocus] || "");
  };

  // Save new training data
  const saveTrainingData = async () => {
    setError("");
    setSuccessMessage("");

    if (!training_focus) {
      setError("Please select a training focus.");
      return;
    }

    try {
      const res = await fetch(
        `${API_URL.POST_RUNROCKET_TRAINING_TEMPLATE_DATA}${template_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify({
            week_number: week,
            day,
            training_focus,
            reps,
            distance,
            rest,
          }),
        },
      );

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to save training data");

      setTrainingFocus("");
      setReps("");
      setDistance("");
      setRest("");
      setSuccessMessage("Training data saved successfully!");
      fetchTrainingData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Edit day focus
  const handleEditFocus = async (day_id) => {
    if (!newFocus || newFocus.trim() === "") return;

    try {
      const res = await fetch(
        `${API_URL.ADMIN_EDIT_RUNROCKET_DAY_FOCUS}${day_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify({
            training_focus: newFocus,
            exercise: newExerciseId || null,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to update training focus");

      setSuccessMessage("Training focus updated successfully!");
      setEditingFocusId(null);
      setNewExerciseId(null);
      fetchTrainingData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete set
  /* const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`${API_URL.DELETE_RUNROCKET_TRAINING_TEMPLATE_SET}${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete training data");

      setSuccessMessage("Training data deleted successfully!");
      setDeleteId(null);
      fetchTrainingData();
    } catch (err) {
      setError(err.message);
    }
  };*/

  const handleDelete = async (id) => {
    if (!id) return;

    try {
      const res = await fetch(
        `${API_URL.DELETE_RUNROCKET_TRAINING_TEMPLATE_SET}${id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to delete training data");

      setSuccessMessage("Training data deleted successfully!");
      fetchTrainingData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 bg-bgBase p-5 relative overflow-auto">
        <button
          onClick={() => navigate(-1)}
          className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:pl-12 mb-5"
        >
          <GoArrowLeft color="#666666" /> Back To Library
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* LEFT: Training Data */}
          <div className="max-lg:order-2 lg:col-span-7">
            <div className="bg-white border border-tblBorder p-5 pt-0 rounded-14 lg:max-h-[870px] overflow-y-auto">
              <div className="py-5 mb-5 border-b bg-white lg:sticky lg:top-0 z-10">
                <h2 className="font-inter font-medium text-2xl text-dark">
                  Training Data
                </h2>
              </div>

              <div className="md:flex-1">
                {isLoading ? (
                  <div className="bg-white border border-tblBorder rounded-md w-fit mx-auto py-2 px-5">
                    <p className="text-dark text-sm font-inter font-normal text-center">
                      Loading training data...
                    </p>
                  </div>
                ) : trainingData.length === 0 ? (
                  <div className="bg-white border border-tblBorder rounded-md w-fit mx-auto py-2 px-5">
                    <p className="text-dark text-sm font-inter font-normal text-center">
                      No training data available.
                    </p>
                  </div>
                ) : (
                  trainingData.map((weekData) => (
                    <div key={weekData.week_number} className="mb-6">
                      <h3 className="font-inter text-lg font-semibold text-dark bg-tblHead p-2 rounded">
                        {weekData.week_number}
                      </h3>

                      {weekData.days.map((dayData) => (
                        <div key={dayData._id || dayData.day} className="mt-4">
                          <div className="w-full flex items-center justify-between mb-2">
                            <h4 className="w-full font-inter text-sm font-semibold text-dark">
                              {dayData.day}{" "}
                              {editingFocusId === dayData._id ? (
                                <div className="mt-3 w-full flex flex-col gap-2">
                                  <select
                                    value={newFocus}
                                    onChange={(e) =>
                                      setNewFocus(e.target.value)
                                    }
                                    className="w-full bg-white border border-tblBorder p-3 rounded-10 text-sm font-inter font-normal leading-tight outline-none"
                                  >
                                    <option value="">
                                      Select Training Focus
                                    </option>
                                    <option value="Technical">Technical</option>
                                    <option value="Speed - Strength">
                                      Speed - Strength
                                    </option>
                                    <option value="Power">Power</option>
                                    <option value="Strength - Speed">
                                      Strength - Speed
                                    </option>
                                  </select>

                                  <SingleSelectExerciseList
                                    exercises={exercises}
                                    value={newExerciseId}
                                    onChange={setNewExerciseId}
                                  />

                                  <div className="flex gap-2">
                                    <button
                                      onClick={() =>
                                        handleEditFocus(dayData._id)
                                      }
                                      className="bg-green-200 rounded py-0.5 px-2 text-green-600 hover:text-green-700 font-inter font-normal text-sm"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingFocusId(null)}
                                      className="bg-gray-200 rounded py-0.5 px-2 text-gray-500 hover:text-gray-600 font-inter font-normal text-sm"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  [{dayData.training_focus}]
                                  {dayData.exercise &&
                                    (() => {
                                      const ex = exercises.find(
                                        (e) => e._id === dayData.exercise,
                                      );
                                      if (ex) {
                                        return (
                                          <p className="text-xs text-gray-500">
                                            🎥 Exercise: {ex.title} |{" "}
                                            <a
                                              href={ex.video_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-500 font-inter font-normal text-sm underline"
                                            >
                                              Watch Video
                                            </a>
                                          </p>
                                        );
                                      }
                                    })()}
                                </>
                              )}
                            </h4>

                            {editingFocusId !== dayData._id && (
                              <button
                                onClick={() => {
                                  setEditingFocusId(dayData._id);
                                  setNewFocus(dayData.training_focus);
                                  setNewExerciseId(dayData.exercise || null);
                                }}
                                className="p-2 rounded-md hover:bg-slate-100"
                              >
                                <LiaEditSolid color="#666666" size={20} />
                              </button>
                            )}
                          </div>

                          <div className="space-y-2">
                            {dayData.sets?.map((set) => (
                              <div
                                key={set._id}
                                className="flex items-center justify-between bg-tblHead p-2 rounded-md border text-gray-700 font-inter font-normal text-sm"
                              >
                                <p>
                                  Reps: {set.reps} | Distance: {set.distance}m
                                </p>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        "/administrator/runrocket/templates/edit-set/" +
                                          set._id,
                                      )
                                    }
                                    className="p-2 rounded-md hover:bg-slate-100"
                                  >
                                    <LiaEditSolid color="#666666" size={20} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(set._id)}
                                    className="p-2 rounded-md hover:bg-slate-100"
                                  >
                                    <RiDeleteBin4Line
                                      color="#666666"
                                      size={20}
                                    />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Add / Save Training Data */}
          <div className="max-lg:order-1 lg:col-span-5">
            <div className="bg-white border border-tblBorder p-5 rounded-14">
              <h2 className="font-inter font-medium text-2xl text-dark mb-4">
                Training Focus:{" "}
                {templates.find((t) => t._id === template_id)?.title}
              </h2>

              {successMessage && (
                <div className="bg-green-100 p-2 mb-2 text-center rounded">
                  <p className="text-green-700  font-inter font-normal">
                    {successMessage}
                  </p>
                </div>
              )}
              {error && (
                <div className="bg-red-100 p-2 mb-2 text-center rounded">
                  <p className="text-red-700 font-inter font-normal">{error}</p>
                </div>
              )}

              <div className="space-y-3 md:space-y-5 my-5">
                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Week
                  </label>
                  <select
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={week}
                    onChange={(e) => setWeek(e.target.value)}
                  >
                    {Array.from({ length: 8 }, (_, i) => `Week ${i + 1}`).map(
                      (w) => (
                        <option key={w} value={w}>
                          {w}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Day
                  </label>
                  <select
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                  >
                    {[
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ].map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Training Focus <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={training_focus}
                    onChange={handleTrainingFocusChange}
                  >
                    <option value="">Select</option>
                    <option value="Technical">Technical</option>
                    <option value="Speed - Strength">Speed - Strength</option>
                    <option value="Power">Power</option>
                    <option value="Strength - Speed">Strength - Speed</option>
                  </select>
                </div>

                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Reps
                  </label>
                  <input
                    type="text"
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Distance
                  </label>
                  <input
                    type="text"
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block font-inter text-slate100 text-sm font-medium">
                    Rest
                  </label>
                  <textarea
                    className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                    value={rest}
                    onChange={(e) => setRest(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-start">
                <button
                  onClick={saveTrainingData}
                  className="px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
